import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';

const ContactForm = ({ noBackground }) => {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [captchaComplete, setCaptchaComplete] = useState(false);

  // Use the reCAPTCHA site key from environment variable
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_WEBSITE_CAPTCHA || process.env.VITE_WEBSITE_CAPTCHA || '';

  // Dynamically load the reCAPTCHA script if not present
  useEffect(() => {
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  // Register a callback for reCAPTCHA completion
  useEffect(() => {
    window.onCaptchaSuccess = function () {
      setCaptchaComplete(true);
    };
    window.onCaptchaExpired = function () {
      setCaptchaComplete(false);
    };
    // If the widget is already rendered, reset state
    setCaptchaComplete(false);
  }, []);

  // Today's date for subject
  const today = new Date();
  const dateString = today.toLocaleDateString('de-DE');

  // Translations
  const labels = {
    name: t('contactFormName'),
    phone: t('contactFormPhone'),
    email: t('contactFormEmail'),
    message: t('contactFormMessage'),
    send: t('contactFormSend'),
    sent: t('contactFormSent'),
    captcha: t('contactFormCaptcha'),
    required: t('contactFormRequired'),
    error: t('contactFormError'),
  };

  // Styling
  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    background: noBackground ? 'none' : '#f8f8f8',
    borderRadius: noBackground ? '0' : '10px',
    padding: '1rem',
    fontFamily: 'Sora, sans-serif',
    color: '#666666',
    boxShadow: noBackground ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
    width: '100%'
  };
  const inputStyle = {
    padding: '8px',
    border: '1px solid #D4D0CD',
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    color: '#666666',
  };
  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: '#666666',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    letterSpacing: '1px',
    transition: 'background 0.2s',
    opacity: 1,
  };
  const buttonDisabledStyle = {
    ...buttonStyle,
    backgroundColor: '#cccccc',
    color: '#888888',
    cursor: 'not-allowed',
    opacity: 0.7,
  };
  const labelStyle = {
    fontWeight: 400,
    fontSize: '1rem',
    marginBottom: '0.2rem',
  };
  const errorStyle = {
    color: '#b90000',
    fontSize: '0.95rem',
    marginTop: '-10px',
    marginBottom: '10px',
  };

  // Check if all required fields are filled
  const isFormValid =
    formData.name.trim() &&
    formData.phone.trim() &&
    formData.email.trim() &&
    formData.message.trim();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission via fetch to prevent redirect
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitted(false);
    const form = e.target;
    const formDataObj = new FormData(form);
    // Add subject, template, honeypot, captcha, blacklist
    formDataObj.set('_subject', `Neue Anfrage von der Webseite von ${formData.name}`);
    formDataObj.set('_template', 'table');
    formDataObj.set('_captcha', 'false');
    formDataObj.set('_blacklist', 'g-recaptcha-response');
    try {
      const response = await fetch('https://formsubmit.co/info@uweveronik.de', {
        method: 'POST',
        body: formDataObj,
        headers: {
          'Accept': 'application/json',
        },
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setCaptchaComplete(false);
        if (window.grecaptcha) window.grecaptcha.reset();
      } else {
        setError(labels.error);
      }
    } catch (err) {
      setError(labels.error);
    }
  };

  // Formsubmit: https://formsubmit.co/
  // Use _subject and _template for custom subject and layout
  // Use _honey for honeypot
  // Use _captcha for Formsubmit's own captcha, but we add Google reCAPTCHA for extra security

  return (
    <form
      style={formStyle}
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      {/* Custom subject for the email */}
      <input type="hidden" name="_subject" value={`Neue Anfrage von der Webseite von ${formData.name}`} />
      {/* Redirect to thank you page or show message */}
      <input type="hidden" name="_template" value="table" />
      {/* Honeypot field for spam bots */}
      <input type="text" name="_honey" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
      {/* Disable Captcha by Formsubmit, we use Google reCAPTCHA */}
      <input type="hidden" name="_captcha" value="false" />
      {/* Prevent g-recaptcha-response from being included in the email */}
      <input type="hidden" name="_blacklist" value="g-recaptcha-response" />

      <label style={labelStyle} htmlFor="name">{labels.name} *</label>
      <input style={inputStyle} type="text" id="name" name="name" required autoComplete="name" value={formData.name} onChange={handleChange} />

      <label style={labelStyle} htmlFor="phone">{labels.phone} *</label>
      <input style={inputStyle} type="tel" id="phone" name="phone" autoComplete="tel" required value={formData.phone} onChange={handleChange} />

      <label style={labelStyle} htmlFor="email">{labels.email} *</label>
      <input style={inputStyle} type="email" id="email" name="email" required autoComplete="email" value={formData.email} onChange={handleChange} />

      <label style={labelStyle} htmlFor="message">{labels.message} *</label>
      <textarea style={{...inputStyle, minHeight: '120px', resize: 'vertical'}} id="message" name="message" required value={formData.message} onChange={handleChange} />

      {/* Google reCAPTCHA v2 */}
      <div style={{margin: '0.5rem 0'}}>
        <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} data-callback="onCaptchaSuccess" data-expired-callback="onCaptchaExpired"></div>
      </div>

      <button
        type="submit"
        style={isFormValid && captchaComplete ? buttonStyle : buttonDisabledStyle}
        disabled={!(isFormValid && captchaComplete)}
      >
        {labels.send}
      </button>
      {submitted && <div style={{color: '#4caf50', marginTop: '10px'}}>{labels.sent}</div>}
      {error && <div style={errorStyle}>{error}</div>}
    </form>
  );
};

export default ContactForm; 